import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import { getOrderInfo } from "../../../services/Admin/OrdersApi";
import { getImageUrl } from "../../../services/indexMediaBaseUrl";

const OrderDetailsDialog = ({ open, onClose, orderId }) => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && orderId) fetch();
    else setOrder(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  const fetch = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await getOrderInfo(orderId);
      const data = res?.success ? res.data : res;
      setOrder(data || null);
    } catch (err) {
      setError(err?.message || "فشل تحميل تفاصيل الطلب");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (v) => {
    if (v == null || v === "") return "-";
    const n = Number(v);
    return Number.isNaN(n) ? v : n.toFixed(2);
  };

  // عرض التاريخ بصيغة 12 ساعة
  const formatDate12 = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>تفاصيل الطلب #{order?.order_number || orderId}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 2 }}>{error}</Typography>
        ) : !order ? (
          <Typography color="text.secondary">لا توجد بيانات</Typography>
        ) : (
          <Stack spacing={2} sx={{ py: 1 }}>
            <Paper sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6">#{order.order_number}</Typography>
                  <Typography variant="caption" color="text.secondary">انشئ: {formatDate12(order.created_at)}</Typography>
                </Box>
                <Box>
                  <Typography>الحالة: {order.status}</Typography>
                  <Typography>المجموع: {formatMoney(order.total)}</Typography>
                  <Typography>طريقة الدفع: {order.payment_method} — حالة الدفع: {order.payment_status}</Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Checkout</Typography>
              <Divider sx={{ my: 1 }} />
              {order.check_out ? (
                <>
                  <Typography>Phone: {order.check_out.phone}</Typography>
                  <Typography>Address: {order.check_out.address || "-"}</Typography>
                  <Typography>Postal: {order.check_out.postal_code || "-"}</Typography>
                  <Typography>Notes: {order.check_out.notes || "-"}</Typography>
                </>
              ) : (
                <Typography color="text.secondary">لا توجد بيانات checkout</Typography>
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Items</Typography>
              <Divider sx={{ my: 1 }} />
              {Array.isArray(order.items) && order.items.length ? (
                order.items.map((it) => {
                  const p = it.product || {};
                  return (
                    <Box key={it.id} sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: "background.paper", boxShadow: 0 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ width: 120, flexShrink: 0 }}>
                          {p.main_photo ? (
                            <img src={getImageUrl(p.main_photo)} alt={p.name_ar || p.name_en} style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 6 }} />
                          ) : p.images && p.images[0] ? (
                            <img src={getImageUrl(p.images[0].image)} alt={p.images[0].title_ar || p.images[0].title_en} style={{ width: "100%", height: 100, objectFit: "cover", borderRadius: 6 }} />
                          ) : null}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700 }}>
                            {p.name_ar || p.name_en || "—"} {p.id ? <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: "text.secondary" }}>#{p.id}</Typography> : null}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{p.desc_ar || p.desc_en || "-"}</Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">الكمية في الطلب: {it.quantity} — السعر: {formatMoney(it.price)} — الإجمالي: {formatMoney(it.total)}</Typography>
                            <Typography variant="body2">المخزون: {p.quantity ?? "-" } — الحالة: {p.status ? "نشط" : "غير نشط"}</Typography>
                            <Typography variant="body2">الفئة: {p.category_id ?? "-"}</Typography>
                          </Box>

                          {/* المقاس المطلوب */}
                          {it.size && (
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>المقاس المطلوب:</Typography>
                              <Box sx={{ px: 1.5, py: 0.5, border: "1px solid", borderColor: "primary.main", borderRadius: 1 }}>
                                <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                  {it.size.size || it.size.key} ({it.size.key})
                                </Typography>
                              </Box>
                            </Box>
                          )}

                          {/* اللون المطلوب */}
                          {it.color && (
                            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>اللون المطلوب:</Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1 }}>
                                <Box sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: it.color.code || "#ccc", border: "1px solid #ddd" }} />
                                <Typography variant="body2">{it.color.color_ar || it.color.color_en}</Typography>
                                {it.color.code && <Typography variant="caption" color="text.secondary">({it.color.code})</Typography>}
                              </Box>
                            </Box>
                          )}

                          {/* images */}
                          {Array.isArray(p.images) && p.images.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="subtitle2">صور المنتج</Typography>
                              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                                {p.images.map((img) => (
                                  <Box key={img.id} sx={{ width: 80, height: 80 }}>
                                    <img src={getImageUrl(img.image)} alt={img.title_ar || img.title_en} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 4 }} />
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {/* features */}
                          {Array.isArray(p.features) && p.features.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="subtitle2">الميزات</Typography>
                              {p.features.map((f) => (
                                <Box key={f.id} sx={{ mt: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{f.title_ar || f.title_en}</Typography>
                                  <Typography variant="caption" color="text.secondary">{f.desc_ar || f.desc_en}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })
              ) : (
                <Typography color="text.secondary">لا توجد عناصر</Typography>
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2">Payments</Typography>
              <Divider sx={{ my: 1 }} />
              {Array.isArray(order.payments) && order.payments.length ? order.payments.map(p => (
                <Box key={p.id} sx={{ mb: 1 }}>
                  <Typography>#{p.id} — {p.payment_method} — {formatMoney(p.amount)}</Typography>
                  <Typography variant="caption" color="text.secondary">حالة: {p.status}</Typography>
                </Box>
              )) : <Typography color="text.secondary">لا توجد مدفوعات</Typography>}
            </Paper>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إغلاق</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsDialog;
